import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FLAG_LABELS, useFeatureFlags } from "shared/context/FeatureFlags";
import type { FeatureFlags } from "shared/context/FeatureFlags";

/**
 * Floating toolbar shown on local and integration (`VITE_SHOW_DEV_TOOLBAR="true"`),
 * hidden on staging/production. Persists flag state to localStorage so toggles survive page reloads.
 *
 * Mount once near the root (e.g. index.tsx) — it renders nothing when the flag is off.
 */
export default function FeatureFlagDevToolbar() {
  if (import.meta.env.VITE_SHOW_DEV_TOOLBAR !== "true") return null;

  return <Toolbar />;
}

function Toolbar() {
  const { flags, setFlag } = useFeatureFlags();
  const [open, setOpen] = useState(false);

  const flagKeys = Object.keys(FLAG_LABELS) as (keyof FeatureFlags)[];
  const activeCount = flagKeys.filter((k) => flags[k]).length;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col items-end gap-2">
      {open && (
        <div className="w-64 bg-white border border-gray-200 shadow-xl rounded-xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <span className="text-xs font-bold tracking-wide text-gray-500 uppercase">
              Feature Flags
            </span>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <FontAwesomeIcon icon="xmark" className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="px-4 py-1 divide-y divide-gray-100">
            {flagKeys.map((key) => (
              <li key={key} className="flex items-center justify-between py-2.5">
                <span className="text-sm text-gray-700">{FLAG_LABELS[key]}</span>
                <button
                  role="switch"
                  aria-checked={flags[key]}
                  onClick={() => setFlag(key, !flags[key])}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    flags[key] ? "bg-blue-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                      flags[key] ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
              </li>
            ))}
          </ul>
          <p className="px-4 pb-3 pt-1 text-[11px] text-gray-400">
            Stored in localStorage · dev only
          </p>
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        aria-label="Toggle feature flags"
        className="flex items-center justify-center text-white bg-gray-900 rounded-full shadow-lg h-9 w-9 hover:bg-gray-700"
      >
        {activeCount > 0 && !open ? (
          <span className="text-xs font-bold">{activeCount}</span>
        ) : (
          <FontAwesomeIcon icon="flag" className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}
